import HealthSourceInfo from "@/components/health/HealthSourceInfo";
import { Flame } from "lucide-react";

export default function DailyMacroSummary({
  consumedCalories = 0,
  targetCalories = 0,
  consumedProtein = 0,
  targetProtein = 0,
  consumedCarbs = 0,
  targetCarbs = 0,
  consumedFat = 0,
  targetFat = 0,
}) {
  const caloriePercent =
    targetCalories > 0 ? Math.min(100, Math.round((consumedCalories / targetCalories) * 100)) : 0;
  const proteinPercent =
    targetProtein > 0 ? Math.min(100, Math.round((consumedProtein / targetProtein) * 100)) : 0;
  const carbsPercent =
    targetCarbs > 0 ? Math.min(100, Math.round((consumedCarbs / targetCarbs) * 100)) : 0;
  const fatPercent =
    targetFat > 0 ? Math.min(100, Math.round((consumedFat / targetFat) * 100)) : 0;

  return (
    <div
      className="p-4 rounded-2xl bg-gradient-to-br from-[#08152B] to-[#040C1A] border border-white/10 shadow-md space-y-3"
      data-testid="daily-macro-summary"
    >
      {/* Daily Calories Progress */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-slate-400 uppercase">
            <Flame className="w-3.5 h-3.5 text-orange-400" />
            <span>Daily Calories</span><HealthSourceInfo topic="tracking" label="Meal calorie target and sources" />
          </div>
          <div className="text-xs font-bold text-white">
            <span className="text-sky-400">{consumedCalories.toLocaleString()}</span>
            <span className="text-slate-400 font-normal">
              {" "}
              / {targetCalories > 0 ? targetCalories.toLocaleString() : "—"} kcal
            </span>
          </div>
        </div>

        {/* Horizontal Calorie Bar */}
        <div className="w-full h-2.5 rounded-full bg-white/5 overflow-hidden border border-white/5 relative">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 transition-all duration-500 shadow-[0_0_10px_rgba(56,189,248,0.5)]"
            style={{ width: `${caloriePercent}%` }}
          />
        </div>
      </div>

      {/* 3-Column Macro Tracking */}
      <div className="grid grid-cols-3 gap-2 pt-1 border-t border-white/5">
        {/* Protein Column */}
        <div className="p-2 sm:p-2.5 min-w-0 rounded-xl bg-white/[0.03] border border-white/5 space-y-1 text-center">
          <span className="text-[10px] font-bold tracking-normal text-emerald-400 uppercase flex items-center justify-center whitespace-nowrap">
            Protein<HealthSourceInfo compact topic="tracking" label="Protein meal target and sources" />
          </span>
          <div className="text-xs font-bold text-white leading-tight">
            {consumedProtein}g
            <span className="text-[10px] font-normal text-slate-400 block">
              / {targetProtein > 0 ? `${targetProtein}g` : "—"}
            </span>
          </div>
          <div className="w-full h-1 rounded-full bg-white/5 overflow-hidden mt-1">
            <div
              className="h-full rounded-full bg-emerald-400 transition-all"
              style={{ width: `${proteinPercent}%` }}
            />
          </div>
        </div>

        {/* Carbs Column */}
        <div className="p-2 sm:p-2.5 min-w-0 rounded-xl bg-white/[0.03] border border-white/5 space-y-1 text-center">
          <span className="text-[10px] font-bold tracking-normal text-sky-400 uppercase flex items-center justify-center whitespace-nowrap">
            Carbs<HealthSourceInfo compact topic="tracking" label="Carbs meal target and sources" />
          </span>
          <div className="text-xs font-bold text-white leading-tight">
            {consumedCarbs}g
            <span className="text-[10px] font-normal text-slate-400 block">
              / {targetCarbs > 0 ? `${targetCarbs}g` : "—"}
            </span>
          </div>
          <div className="w-full h-1 rounded-full bg-white/5 overflow-hidden mt-1">
            <div
              className="h-full rounded-full bg-sky-400 transition-all"
              style={{ width: `${carbsPercent}%` }}
            />
          </div>
        </div>

        {/* Fat Column */}
        <div className="p-2 sm:p-2.5 min-w-0 rounded-xl bg-white/[0.03] border border-white/5 space-y-1 text-center">
          <span className="text-[10px] font-bold tracking-normal text-amber-400 uppercase flex items-center justify-center whitespace-nowrap">
            Fat<HealthSourceInfo compact topic="tracking" label="Fat meal target and sources" />
          </span>
          <div className="text-xs font-bold text-white leading-tight">
            {consumedFat}g
            <span className="text-[10px] font-normal text-slate-400 block">
              / {targetFat > 0 ? `${targetFat}g` : "—"}
            </span>
          </div>
          <div className="w-full h-1 rounded-full bg-white/5 overflow-hidden mt-1">
            <div
              className="h-full rounded-full bg-amber-400 transition-all"
              style={{ width: `${fatPercent}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
